import { memo, type ReactElement, useContext } from 'react';
import styled, { type CSSObject } from '@emotion/styled';

import { WWContext, type WWContextValue } from '../WWContext';
import type { ThemeProps } from '@emotion/react';
import Theme from './Theme';
import TopMatter from './TopMatter';
import { isWwsWeddingWebsiteV2 } from '../../types/graphql';
import Footer from './Footer/Standard/Footer';
import PublishBanner from './PublishBanner/PublishBanner';

interface Props {
  children: ReactElement;
  isHomePage?: boolean;
}

const HomeWrapper = styled('div')`
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;

  container-type: inline-size;

  ${({ theme }: ThemeProps): CSSObject | undefined =>
    theme.components.SiteContent}
`;

const Content = styled('div')`
  padding: 16px;
  display: flex;
  flex-direction: column;

  .verticalLayout.homePage & {
    width: 100%;

    @media (min-width: 768px) {
      padding: 16px 2rem;
    }

    @media (min-width: 1024px) {
      padding: 16px 5rem;
    }
  }
}
`;

function Layout({ children, isHomePage = false }: Props) {
  const { data } = useContext<WWContextValue>(WWContext);

  if (!isWwsWeddingWebsiteV2(data?.weddingWebsiteResult)) {
    return null;
  }

  const isVerticalPhoto = (
    cropBox: string,
    width: number,
    height: number
  ): boolean => {
    const imageCoordinates = cropBox.split('.').map(Number);

    if (cropBox !== '0.0.0.0') {
      // get the cropbox coordinates
      // default values to horizontal if not available
      const cropTopLeftX = imageCoordinates[0] || 0;
      const cropTopLeftY = imageCoordinates[1] || 0;
      const cropBottomLeftY = imageCoordinates[3] || 1;
      const cropBottomRightX = imageCoordinates[2] || 2;

      // calculate if cropbox is horizontal or vertical;
      return cropBottomRightX - cropTopLeftX < cropBottomLeftY - cropTopLeftY;
    }

    // if no cropbox is present use the ref width and height
    return width < height;
  };

  const coverPhoto = data?.weddingWebsiteResult.coverPhoto;

  // use crop box to check horizontal vs vertical since it ignores rotation
  // default to horizontal by passing in 0.0.2.1
  const isVerticalLayout =
    coverPhoto &&
    isVerticalPhoto(
      coverPhoto.cropBox || '0.0.2.1',
      coverPhoto.width || 2,
      coverPhoto.height || 1
    );

  const wrapperClasses = `${
    isVerticalLayout ? 'verticalLayout' : 'horizontalLayout'
  } ${isHomePage ? 'homePage' : 'secondaryPage'}`;

  return (
    <>
      <PublishBanner />
      <Theme footer={<Footer />}>
        <HomeWrapper className={wrapperClasses}>
          <TopMatter />

          <Content>{children}</Content>
        </HomeWrapper>
      </Theme>
    </>
  );
}

export default memo(Layout);
